import { NgModule, inject, provideAppInitializer } from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {AuthService} from './services/auth/auth.service';
import {RegionService} from './services/region/region.service';
import {HeaderModule} from "./components/header/header.module";
import {BackToComponent} from './components/back-to/back-to.component';
import {RequestsModule} from './pages/requests/requests.module';
import {PeopleModule} from './pages/people/people.module';
import {FilterV3Module} from 'src/app/components/filter-v3/filter-v3.module';
import {QuestionnairesComponent} from './pages/questionnaires/questionnaires.component';
import {CheckStateIconComponent} from './components/check-state-icon/check-state-icon.component';
import {CheckIconComponent} from './components/check-icon/check-icon.component';
import {JWT_OPTIONS, JwtModule} from "@auth0/angular-jwt";
import {QuestionnaireModule} from './modules/questionnaire/questionnaire.module';
import {QuestionnaireService} from './services/questionnaire/questionnaire.service';
import {ProfileModule} from "./pages/profile/profile.module";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import {AccountManagementModule} from "./pages/account-management/account-management.module";
import {ModalModule} from "ngx-bootstrap/modal";
import {OrganisationService} from "./services/organisation/organisation.service";
import {CanDeactivateGuard} from "./services/can-deactivate-guard/can-deactivate.guard";
import {WarningModalModule} from "./lib/warning-modal/warning-modal.module";
import {AlertBannerModule} from "./lib/alert-banner";
import {PasswordValidatorModule} from "./lib/password-validator/password-validator.module";
import {EmailRegexValidatorModule} from "./lib/email-regex-validator/email-regex-validator.module";
import {AlertMessageModule} from "./lib/alert-message/alert-message.module";
import {InsightsModule} from "./pages/insights/insights.module";
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {ReportModule} from "./pages/report/report.module";
import {PeopleProfileModule} from "./pages/people-profile/people-profile.module";
import {SendXrefButtonModule} from "./components/send-xref-button/send-xref-button.module";
import {SendXrefModule} from "./components/send-xref/send-xref.module";
import {AuthInterceptor} from "./services/interceptor/http-interceptor.service";
import {jwtGetter} from "./shared/jwt-getter";
import {ConstantsService} from "./services/constants/constants.service";
import {QuestionnaireUserPermissionModalComponent} from './components/questionnaire-user-permission-modal/questionnaire-user-permission-modal.component';
import {FilterUsersModule} from "./components/filter-users/filter-users.module";
import {PreviewQuestionnaireModule} from './components/preview-questionnaire/preview-questionnaire.module';
import { LoaderModule } from "./components/loader/loader.module";
import {TabsModule} from 'ngx-bootstrap/tabs';
import {PeopleService} from './services/people/people.service';
import {AppPaginationModule} from "./components/pagination/pagination.module";
import {SortModule} from "./components/sort/sort.module";
import {SortV2Module} from "./components/sort-v2/sort-v2.module";
import {EditStatusModalModule} from "./components/edit-status-modal/edit-status-modal.module";
import {FooterModule} from "./components/footer/footer.module";
import {SearchModule} from "./components/search/search.module";
import {SearchCoreModule} from "./components/search-core/search-core.module";
import {SearchCoreChipDisplayModule} from "./components/search-core-chip-display/search-core-chip-display.module";
import {AlertModule} from 'ngx-bootstrap/alert';
import {NgSelectModule} from "@ng-select/ng-select";
import {EmailSmsTemplateModule} from "./pages/email-sms-template/email-sms-template.module";
import {CreateTemplateModule} from "./pages/create-template/create-template.module";
import {AvatarIconModule} from './components/avatar-icon/avatar-icon.module';
import {PermissionsService} from "./services/permissions/permissions.service";
import {TooltipEllipsifyModule} from './directives/tooltip-ellipsify/tooltip-ellipsify.module';
import * as Cookies from "js-cookie";
import {Init} from "./app.init";
import {environment} from "../environments/environment";
import {ApiModule} from "./services/api/api.module";
import {ReferenceReportModule} from "./pages/reference-report/reference-report.module";
import {ReferenceReportContentModule} from "./components/reference-report-content/reference-report-content.module";
import { SurveyService } from './services/survey/survey.service';
import { CreateSurveyModule } from './components/create-survey/create-survey.module';
import { CreateSurveyFromScratchModule } from './components/create-survey-from-scratch/create-survey-from-scratch.module';
import { CreateReferenceSurveyModule } from './components/create-reference-survey/create-reference-survey.module';
import { PlanService } from './services/plan-status/plan-status.service';
import { SelectCheckTypeModalComponent } from './components/select-check-type-modal/select-check-type-modal.component';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { ReferencesReportHelpModalComponent } from './components/references-report-help-modal/references-report-help-modal.component';
import {
  BannerModule,
  ButtonModule,
  DropdownModule,
  InputsModule,
  CheckboxModule,
  FilterDropdownModule,
  GroupDropdownModule,
  ToggleModule, RadioModule,
  CardsModule, EmptyStateModule,
  TooltipModule as XrefTooltipModule,
  DynamicGroupDropdownModule,
  KebabMenuModule,
  ProductToggleModule,
  TemplateCardModule
} from '@xref/xref-lib';
import { AddEditDepartmentComponent } from './components/add-edit-department/add-edit-department.component';
import { DepartmentHelpModalComponent } from './components/department-help-modal/department-help-modal.component';
import { PulsesHelpModalComponent } from './components/pulses-help-modal/pulses-help-modal.component';
import { PulseRequestsViewRecipientsComponent } from './components/pulse-requests-view-recipients/pulse-requests-view-recipients.component';
import { TrustApiKeyModalComponent } from './components/trust-api-key-modal/trust-api-key-modal.component';
import { TrustApiKeyUpdateModalComponent } from './components/trust-api-key-modal/trust-api-key-update-modal/trust-api-key-update-modal.component';
import { TrustApiKeyDeleteConfirmationModalComponent } from './components/trust-api-key-modal/trust-api-key-delete-confirmation-modal/trust-api-key-delete-confirmation-modal.component';
import { TrustApiKeyCancelConfirmationModalComponent } from './components/trust-api-key-modal/trust-api-key-cancel-confirmation-modal/trust-api-key-cancel-confirmation-modal.component';
import { TrustApiKeyHelpConfigureModalComponent } from './components/trust-api-key-modal/trust-api-key-help-configure-modal/trust-api-key-help-configure-modal.component';
import { TrustApiKeyHelpUpdateModalComponent } from './components/trust-api-key-modal/trust-api-key-help-update-modal/trust-api-key-help-update-modal.component';
import {XrefLibFeatureFlagService, TagsModule, EmptyStateSearchModule} from '@xref/xref-lib';
import { TeamsAddEditComponent } from './components/teams-add-edit/teams-add-edit.component';
import { TeamsManageUsersComponent } from './components/teams-manage-users/teams-manage-users.component';
import { TeamsRemoveTeamComponent } from './components/teams-remove-team/teams-remove-team.component';
import {FilterPopupModule} from "./components/filter-popup/filter-popup.module";
import { NewManageUserComponent } from './components/org-users/components/new-manage-user/new-manage-user.component';
import {SurveyHelpModalComponent} from "./components/survey-help-modal/survey-help-modal.component";
import {
  SurveyUserPermissionModalModule
} from "./components/survey-user-permission-modal/survey-user-permission-modal.module";
import { BillingModule } from "./components/billing/billing.module";
import {TalentPoolReportModalModule} from "./components/talent-pool-report-modal/talent-pool-report-modal.module";
import { G2FormsModalModule } from './components/g2-forms-modal/g2-forms-modal.module';

let apiRegion = environment.use_global_id ? Cookies.get(ConstantsService.API_REGION_TOKEN) : Cookies.get(ConstantsService.DEPRECATED_API_REGION_TOKEN);

const apiConfig = {
  url: (apiRegion ? new Init().makeRegionalApiUrl(apiRegion) : environment.API)
};

@NgModule({ declarations: [
        AppComponent,
        BackToComponent,
        QuestionnairesComponent,
        CheckStateIconComponent,
        CheckIconComponent,
        QuestionnaireUserPermissionModalComponent,
        SelectCheckTypeModalComponent,
        ReferencesReportHelpModalComponent,
        AddEditDepartmentComponent,
        DepartmentHelpModalComponent,
        PulsesHelpModalComponent,
        SurveyHelpModalComponent,
        PulseRequestsViewRecipientsComponent,
        TrustApiKeyModalComponent,
        TrustApiKeyUpdateModalComponent,
        TrustApiKeyDeleteConfirmationModalComponent,
        TrustApiKeyCancelConfirmationModalComponent,
        TrustApiKeyHelpConfigureModalComponent,
        TrustApiKeyHelpUpdateModalComponent,
        TeamsAddEditComponent,
        TeamsManageUsersComponent,
        TeamsRemoveTeamComponent,
        NewManageUserComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        SurveyUserPermissionModalModule,
        FilterV3Module,
        AppRoutingModule,
        NgbModule,
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory
            }
        }),
        // @ts-ignore
        ApiModule.forRoot(apiConfig),
        ProfileModule,
        RequestsModule,
        QuestionnaireModule,
        FormsModule,
        ReactiveFormsModule,
        ModalModule.forRoot(),
        AccountManagementModule,
        WarningModalModule,
        WarningModalModule,
        AlertBannerModule.forRoot(),
        PasswordValidatorModule,
        EmailRegexValidatorModule,
        AlertMessageModule,
        InsightsModule,
        SendXrefButtonModule,
        ReportModule,
        TabsModule.forRoot(),
        PeopleProfileModule,
        SendXrefModule,
        PaginationModule.forRoot(),
        BrowserAnimationsModule,
        BsDropdownModule.forRoot(),
        TooltipModule.forRoot(),
        AlertModule.forRoot(),
        AccordionModule.forRoot(),
        NgSelectModule,
        EmailSmsTemplateModule,
        CreateTemplateModule,
        AppPaginationModule,
        SortModule,
        CreateSurveyModule,
        SortV2Module,
        EditStatusModalModule,
        FooterModule,
        FilterUsersModule,
        SearchModule,
        SearchCoreModule,
        SearchCoreChipDisplayModule,
        PeopleModule,
        HeaderModule,
        AvatarIconModule,
        PreviewQuestionnaireModule,
        CreateSurveyFromScratchModule,
        CreateReferenceSurveyModule,
        LoaderModule,
        TooltipEllipsifyModule,
        ReferenceReportModule,
        ReferenceReportContentModule,
        BannerModule,
        DropdownModule,
        InputsModule,
        ButtonModule,
        GroupDropdownModule,
        ToggleModule,
        CheckboxModule,
        TagsModule,
        RadioModule,
        EmptyStateSearchModule,
        EmptyStateSearchModule,
        FilterDropdownModule,
        CardsModule,
        EmptyStateModule,
        XrefTooltipModule,
        DynamicGroupDropdownModule,
        KebabMenuModule,
        ProductToggleModule,
        TemplateCardModule,
        TalentPoolReportModalModule,
        BillingModule,
        G2FormsModalModule], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        provideAppInitializer(() => {
        const initializerFn = (permissionsFactory)(inject(PermissionsService));
        return initializerFn();
      }),
        XrefLibFeatureFlagService,
        AuthService,
        RegionService,
        OrganisationService,
        QuestionnaireService,
        PeopleService,
        CanDeactivateGuard,
        SurveyService,
        PlanService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
}

export function jwtOptionsFactory() {
  if (environment.use_global_id) {
    return {
      tokenName: ConstantsService.AUTH_TOKEN,
      headerName: 'X-JWT',
      authScheme: 'JWT ',
      tokenGetter: jwtGetter,
      globalHeaders: [
        {'Content-Type': 'application/json'},
        {'Accept': 'application/json'}
      ],
      noClientCheck: true,
    };
  } else {
    return {
      tokenName: ConstantsService.DEPRECATED_AUTH_TOKEN,
      headerName: 'X-JWT',
      authScheme: 'JWT ',
      tokenGetter: jwtGetter,
      globalHeaders: [
        {'Content-Type': 'application/json'},
        {'Accept': 'application/json'}
      ],
      noClientCheck: true,
    };
  }
}

export function permissionsFactory(provider: PermissionsService) {
  return (): Promise<any> => {
    return provider.initializePermissions();
  }
}
